// src/components/common/DeferredVideo.tsx
//
// Background video that never competes with the first paint: the poster
// renders immediately, the video source is attached only after the page has
// loaded and the browser is idle, and not at all on small screens, on
// data-saver connections, or for people who asked for reduced motion.

import React, { useEffect, useRef, useState } from 'react';
import { Box, SxProps, Theme } from '@mui/material';

interface DeferredVideoProps {
  src: string;
  poster: string;
  sx?: SxProps<Theme>;
  /** Skip the video entirely below this viewport width (px). */
  minWidth?: number;
}

const DeferredVideo: React.FC<DeferredVideoProps> = ({ src, poster, sx, minWidth = 900 }) => {
  const [ready, setReady] = useState(false);
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const nav = navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } };
    const slow = nav.connection?.saveData || /2g/.test(nav.connection?.effectiveType || '');
    if (reduced || slow || window.innerWidth < minWidth) return;

    let cancelled = false;
    const arm = () => {
      const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
        .requestIdleCallback;
      const go = () => !cancelled && setReady(true);
      if (idle) idle(go, { timeout: 2500 });
      else setTimeout(go, 1200);
    };
    if (document.readyState === 'complete') arm();
    else window.addEventListener('load', arm, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener('load', arm);
    };
  }, [minWidth]);

  useEffect(() => {
    if (ready && ref.current) {
      ref.current.play().catch(() => {});
    }
  }, [ready]);

  return (
    <Box
      component="video"
      ref={ref}
      muted
      loop
      playsInline
      preload="none"
      poster={poster}
      src={ready ? src : undefined}
      aria-hidden="true"
      sx={sx}
    />
  );
};

export default DeferredVideo;
